import { InlineValue, resolveLocale, usePageContent } from "@appiodev/xcore-client/xcore-ui";
import { Content } from "@appiodev/xcore-core";
import { Box, Cell, Container, Grid, Img, Stack, Typography } from "@xcorejs/ui";
import ContactMenu from "components/contacts/Menu";
import Layout from "components/Layout";
import RobeHead from "components/RobeHead";
import RobeRichtext from "components/RobeRichtext";
import SmartLink from "components/SmartLink";
import PersonCard from "design-system/robe/cards/Person";
import LeftMenuContainer from "design-system/robe/LeftMenuContainer";
import RobeHeading from "design-system/robe/RobeHeading";
import { FC, useMemo } from "react";
import { useTagManager } from "utils/useTagManager";
import { getRobePaths, getRobeProps, RobePage, useLayout } from "xcore";
import { Office, OfficeValues } from "xcore/types";

export const getStaticProps = getRobeProps<Office>(({ cms, id }) => ({
  pageContent: cms.content.single(id!, {
    preview: true,
    relations: v => [
      v.people?.map(p => p.persons),
      v.detail?.map(d => [
        d.linksEn,
        d.linksDe,
        d.linksEnEN,
        d.linksRu,
        d.linksFr
      ])
    ]
  })
}));

export const getStaticPaths = getRobePaths("office");

const Contact: RobePage = () => {
  const [{ values, ...content }, { stringify, value, file, richtextToString }] = usePageContent<Office>();

  useTagManager("contact");

  return (
    <>
      <RobeHead
        title={[stringify(values.title), "Contacts"]}
        description={richtextToString(values.detail[0].text)}
        img={file(values.detail[0].image, { width: 410, height: 273 })}
      />
      <Container flexDirection="column" paddingTop={{ _: "3rem", sm: "5rem" }} paddingBottom={{ _: "3rem", sm: "5rem" }}>
        <Stack direction="column" gap={{ _: "2rem", sm: "3rem" }}>
          <RobeHeading display={{ _: "none", md: "flex" }} justifyContent="center">
            <InlineValue value={stringify(values.title)} />
          </RobeHeading>

          <LeftMenuContainer
            rightBorder={{ _: 0, md: ".2rem solid #DEE1E5" }}
            contentPadding={{ _: 0, md: "7rem", lg: "14rem" }}
            leftContent={<ContactMenu />}
          >
            <RobeHeading display={{ _: "flex", md: "none" }} justifyContent="center" mb="2.4rem">
              <InlineValue value={stringify(values.title)} />
            </RobeHeading>

            {values.detail.map((s, i) => <InfoSection key={i} content={content} section={s.__handler[0]} />)}

            {values.people && (
              <Box mt="6rem">
                {values.people?.map((d, i) => (
                  <Box key={i} mb="6rem">
                    <Stack direction="column" gap={{ _: "2rem", sm: "3rem" }}>
                      {stringify(d.department) && (
                        <Typography variant="h3" fontWeight={600}>
                          <InlineValue value={d.department} />
                        </Typography>
                      )}

                      <Grid
                        columns={{ _: "1fr", xs: "repeat(2, 1fr)", sm: "repeat(4, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" }}
                        rows="auto"
                        gap="3rem"
                      >
                        {d.persons.map((p, pi) => (
                          <Cell
                            key={pi}
                            display="flex"
                            height="auto"
                            flexDirection="column"
                            justify-content="space-between"
                          >
                            {stringify(p.values.subheadline) && (
                              <Typography
                                variant="h3"
                                mb="2rem"
                                fontWeight={600}
                                fontSize="1.5rem"
                                lineHeight="2.5rem"
                                textTransform="uppercase"
                              >
                                {stringify(p.values.subheadline)}
                              </Typography>
                            )}

                            <PersonCard person={value(p)} />
                          </Cell>
                        ))}
                      </Grid>
                    </Stack>
                  </Box>
                ))}
              </Box>
            )}
          </LeftMenuContainer>
        </Stack>
      </Container>
      {/* {Number(value(values.longitude)) !== 0 && Number(value(values.latitude)) !== 0 && (
        <MapComponent
          height="60rem"
          alignItems="center"
          justifyContent="center"
          positionX={Number(value(values.longitude))}
          positionY={Number(value(values.latitude))}
          url={`https://www.google.com/maps/search/?api=1&query=${value(values.longitude)},${value(values.latitude)}`}
        />
      )} */}
    </>
  );
};

Contact.Layout = Layout;

export default Contact;

// const MapComponent = dynamic(import("components/Map"), {
//   ssr: false,
//   loading: () => (
//     <Flex
//       height="60rem"
//       alignItems="center"
//       justifyContent="center"
//     >
//       <Spinner />
//     </Flex>
//   )
// });

interface InfoSectionProps {
  content: Pick<Content, "publishedAt" | "enabled">;
  // @ts-ignore
  section: OfficeValues["detail"][number]; // TODO NULL-CHECKS: resolve
}

const InfoSection: FC<InfoSectionProps> = ({ section, content }) => {
  const { stringify, file, richtextToString, cms, value } = useLayout();

  const links = useMemo(() => resolveLocale(cms.locales.content, content)({
    "en": section.linksEn,
    "de": section.linksDe,
    "ru": section.linksRu,
    "en-EN": section.linksEnEN,
    "fr": section.linksFr
  }) ?? [], [cms.locales.content, content, section.linksEn, section.linksDe, section.linksRu, section.linksEnEN, section.linksFr]);

  return (
    <Grid
      columns={{ _: "1", sm: section.image ? "auto 41rem" : "1" }}
      rows="auto"
      gap="3rem"
      mt={stringify(section.subtitle).length > 0 ? "3rem" : 0}
      _firstOfType={{
        mt: 0
      }}
    >
      <Box>
        {stringify(section.subtitle).length > 0 && (
          <Typography variant="h3" as="h2" mb="3rem">
            <InlineValue value={section.subtitle} />
          </Typography>
        )}

        {richtextToString(section.text).length > 0 && (
          <Box mb="3rem">
            <RobeRichtext
              value={section.text}
              _paragraph={{ maxWidth: section.image ? "30rem" : "none", minHeight: "2.5rem", wordBreak: "break-word" }}
              _link={{ v: "underline" }}
            />
          </Box>
        )}

        <Stack gap="2rem" direction="column">
          {value(links).map((l, i) => (
            <Box key={i}>
              <SmartLink
                smartLink={l}
                _link={{ fontWeight: 400, alignItems: "center" }}
                _text={{
                  color: "robe",
                  borderBottom: "1px solid #ee0037",
                  transition: "border 300ms",
                  fontSize: "1.4rem",
                  lineHeight: "2.1rem",
                  _group: { _hover: { color: "robe", borderColor: "transparent" } }
                }}
                _iconProps={{
                  _group: { _hover: { _icon: { fill: "primary" } } }
                }}
              />
            </Box>
          ))}
        </Stack>
      </Box>
      {section.image && (
        <Box>
          <Img
            display="block"
            width="100%"
            src={file(section.image, { width: 410, height: 273 })}
            alt={stringify(section.subtitle)}
            loading="lazy"
          />
        </Box>
      )}
    </Grid>
  );
};
